import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, ChevronRight } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { InlineLoading } from '@/components/ui/data-states';
import { useMisAvisosCorreo, type AvisoCorreo } from '@/hooks/useMisAvisosCorreo';
import { enlaceAviso } from '@/lib/notificaciones/enlaceAviso';
import { cn } from '@/lib/utils';

const TITULOS: Record<string, string> = {
  agente_oferta_generada: 'Oferta generada',
  tickets_pendientes_resumen: 'Resumen de tickets pendientes',
  cliente_forma_pago_externa: 'Forma de pago externa por revisar',
  agente_prospecto_registrado: 'Prospecto registrado',
  legal_contrato_firma_cancelada: 'Firma de contrato cancelada',
  legal_contrato_firma_comprador: 'Contrato firmado por el comprador',
  ticket_chat_interno_mensaje: 'Mensaje nuevo en un ticket',
};

function titulo(a: AvisoCorreo): string {
  if (a.nombre?.trim()) return a.nombre.trim();
  if (TITULOS[a.clave]) return TITULOS[a.clave];
  const t = a.clave.replace(/_/g, ' ').trim();
  return t.charAt(0).toUpperCase() + t.slice(1);
}

function detalle(a: AvisoCorreo): string | null {
  if (a.clave === 'tickets_pendientes_resumen' && a.payload) {
    const total = Number(a.payload.total ?? 0);
    const vencidos = Number(a.payload.vencidos ?? 0);
    return `${total} pendientes, ${vencidos} vencidos`;
  }
  return null;
}

function fechaRelativa(f: string): string {
  const d = new Date(f);
  if (Number.isNaN(d.getTime())) return '';
  return formatDistanceToNow(d, { addSuffix: true, locale: es });
}

export function NotificacionesAdmin() {
  const navigate = useNavigate();
  const [abierto, setAbierto] = useState(false);
  const { avisos, sinLeer, isLoading, isError, refetch, estaLeido, marcarLeido, marcarTodos } =
    useMisAvisosCorreo();

  const alCambiar = (v: boolean) => {
    setAbierto(v);
    if (v) void refetch();
  };

  const abrir = (a: AvisoCorreo, ruta: string | null) => {
    marcarLeido(a.id);
    if (ruta) {
      setAbierto(false);
      navigate(ruta);
    }
  };

  const etiqueta =
    sinLeer > 0
      ? `Avisos por correo, ${sinLeer} sin leer`
      : 'Avisos por correo, sin avisos nuevos';

  return (
    <Popover open={abierto} onOpenChange={alCambiar}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative shrink-0" aria-label={etiqueta}>
          <Bell className="h-5 w-5" />
          {sinLeer > 0 && (
            <span
              aria-hidden="true"
              className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold leading-none text-destructive-foreground"
            >
              {sinLeer > 9 ? '9+' : sinLeer}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-[360px] max-w-[calc(100vw-32px)] overflow-hidden p-0"
      >
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <div className="flex flex-col">
            <span className="text-sm font-semibold text-foreground">Avisos por correo</span>
            {sinLeer > 0 && (
              <span className="text-[11px] text-muted-foreground">{sinLeer} sin leer</span>
            )}
          </div>
          {sinLeer > 0 && (
            <button
              type="button"
              onClick={marcarTodos}
              className="flex items-center gap-1 text-[11px] font-medium text-primary hover:underline"
            >
              <Check className="size-3" />
              Marcar todas como leídas
            </button>
          )}
        </div>

        {isLoading ? (
          <InlineLoading label="Cargando avisos..." className="py-10" />
        ) : isError ? (
          <div className="px-6 py-10 text-center">
            <p className="text-sm font-medium text-foreground">No pudimos cargar tus avisos</p>
            <Button variant="link" size="sm" onClick={() => void refetch()}>
              Reintentar
            </Button>
          </div>
        ) : avisos.length === 0 ? (
          <div className="flex flex-col items-center px-6 py-10 text-center">
            <div className="mb-3 flex size-10 items-center justify-center rounded-full bg-muted">
              <Bell className="size-4 text-muted-foreground" />
            </div>
            <p className="text-sm font-medium text-foreground">No tienes avisos por correo</p>
          </div>
        ) : (
          <ScrollArea className="h-[min(420px,60vh)]">
            <ul>
              {avisos.map((a) => {
                const leido = estaLeido(a.id);
                const ruta = enlaceAviso(a);
                const sub = detalle(a);
                return (
                  <li key={a.id} className="border-b border-border/40 last:border-0">
                    <button
                      type="button"
                      onClick={() => abrir(a, ruta)}
                      className={cn(
                        'flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/40',
                        !leido && 'bg-primary/[0.03]',
                      )}
                    >
                      <span
                        aria-hidden="true"
                        className={cn(
                          'mt-1.5 size-1.5 shrink-0 rounded-full',
                          leido ? 'bg-transparent' : 'bg-primary',
                        )}
                      />
                      <div className="min-w-0 flex-1">
                        <p
                          className={cn(
                            'text-xs text-foreground',
                            leido ? 'font-medium' : 'font-bold',
                          )}
                        >
                          {titulo(a)}
                        </p>
                        {sub && <p className="mt-0.5 text-[11px] text-muted-foreground">{sub}</p>}
                        <p className="mt-1 text-[10px] text-muted-foreground">
                          {fechaRelativa(a.fecha)}
                        </p>
                        {ruta && (
                          <span className="mt-1 inline-flex items-center gap-0.5 text-[11px] font-medium text-primary">
                            {a.cta_label?.trim() || 'Ver registro'}
                            <ChevronRight className="size-3" />
                          </span>
                        )}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          </ScrollArea>
        )}
      </PopoverContent>
    </Popover>
  );
}
